/**
 * Page View Service
 *
 * Handles tracking of page views and analytics events.
 * Stores data in Supabase for internal analytics dashboard.
 */

import { supabase } from './supabaseService';
import { getStoredUTMParams } from '../utils/analytics';

/**
 * LGPD gate: page_views grava `visitor_id` durável + UTMs + device fingerprint.
 * Sob LGPD Art. 5°, o `visitor_id` persistente é dado pessoal. Antes do usuário
 * aceitar cookies (ou se ele escolheu "Apenas essenciais"), NÃO escrever.
 *
 * Retorna `true` se tracking está permitido. Lê o mesmo storage key que
 * `CookieConsent.tsx` grava (`clinafy_cookie_consent`).
 */
const hasMarketingConsent = (): boolean => {
  try {
    const raw = localStorage.getItem('clinafy_cookie_consent');
    if (!raw) return false;
    const consent = JSON.parse(raw);
    return consent?.accepted === true && consent?.essential_only !== true;
  } catch {
    return false;
  }
};

// Generate a visitor ID (stored in localStorage for session tracking)
const getVisitorId = (): string => {
  const storageKey = 'clinafy_visitor_id';
  let visitorId = localStorage.getItem(storageKey);

  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(storageKey, visitorId);
  }

  return visitorId;
};

// Generate session ID (resets after 30 min of inactivity)
const getSessionId = (): string => {
  const storageKey = 'clinafy_session_id';
  const timestampKey = 'clinafy_session_timestamp';
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const lastTimestamp = localStorage.getItem(timestampKey);
  const now = Date.now();

  let sessionId = localStorage.getItem(storageKey);

  // Check if session expired
  if (!sessionId || !lastTimestamp || (now - parseInt(lastTimestamp)) > SESSION_TIMEOUT) {
    sessionId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(storageKey, sessionId);
  }

  // Update timestamp
  localStorage.setItem(timestampKey, now.toString());

  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'Internet Explorer';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Detect OS
const getOS = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

export interface PageViewData {
  page_path: string;
  page_title?: string;
  page_referrer?: string;
  event_type?: string;
  event_data?: Record<string, unknown>;
}

export interface AnalyticsEvent {
  event_type: string;
  event_data?: Record<string, unknown>;
  page_path?: string;
}

/**
 * Track a page view
 */
export const trackPageView = async (data: PageViewData): Promise<void> => {
  if (!hasMarketingConsent()) {
    // Não criar visitor_id durável nem POST pra Supabase antes do aceite LGPD
    return;
  }
  try {
    const utmParams = getStoredUTMParams();

    const pageView = {
      page_path: data.page_path,
      page_title: data.page_title || document.title,
      page_referrer: data.page_referrer || document.referrer || null,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      utm_source: utmParams.utm_source || null,
      utm_medium: utmParams.utm_medium || null,
      utm_campaign: utmParams.utm_campaign || null,
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      event_type: data.event_type || 'page_view',
      event_data: data.event_data || {},
    };

    const { error } = await supabase
      .from('page_views')
      .insert(pageView);

    if (error) {
      console.error('Error tracking page view:', error);
    }
  } catch (err) {
    console.error('Error in trackPageView:', err);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = async (event: AnalyticsEvent): Promise<void> => {
  if (!hasMarketingConsent()) {
    return;
  }
  try {
    const utmParams = getStoredUTMParams();

    const eventData = {
      page_path: event.page_path || window.location.pathname + window.location.hash,
      page_title: document.title,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      utm_source: utmParams.utm_source || null,
      utm_medium: utmParams.utm_medium || null,
      utm_campaign: utmParams.utm_campaign || null,
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      event_type: event.event_type,
      event_data: event.event_data || {},
    };

    const { error } = await supabase
      .from('page_views')
      .insert(eventData);

    if (error) {
      console.error('Error tracking event:', error);
    }
  } catch (err) {
    console.error('Error in trackEvent:', err);
  }
};

/**
 * Track CTA click
 */
export const trackCTAClick = (ctaName: string, ctaLocation?: string): void => {
  trackEvent({
    event_type: 'cta_click',
    event_data: {
      cta_name: ctaName,
      cta_location: ctaLocation || 'unknown',
    },
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formName: string, success: boolean): void => {
  trackEvent({
    event_type: 'form_submit',
    event_data: {
      form_name: formName,
      success,
    },
  });
};

/**
 * Track PDF download
 */
export const trackPDFDownload = (pdfName: string): void => {
  trackEvent({
    event_type: 'pdf_download',
    event_data: {
      pdf_name: pdfName,
    },
  });
};

export default {
  trackPageView,
  trackEvent,
  trackCTAClick,
  trackFormSubmit,
  trackPDFDownload,
};
